import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, interval } from 'rxjs';
import { environment } from '@stockcare-environments/environment';
import { ISetting, ISettingGroup, ISettingOption } from '@stockcare-models/ISetting';
import { Settings } from '@stockcare-helpers/settings';
import { switchMap } from 'rxjs/operators';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { ConfigService } from '@stockcare-services/config-service/config.service';
import { IStockLocation } from '@stockcare-models/IStock-location';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  settingsSyncInterval = new Subject();
  applicationUpdateCheckInterval = new Subject();
  constructor(private http: HttpClient, private deviceService: DeviceService, private conf: ConfigService) {}
  settingsUpdated = new Subject<ISetting[]>();

  initialize(): Promise<void> {
    return new Promise((resolve) => {
      console.log('Starting setting service');
      const settings = this.getDefaultSettings();
      this.saveToLocalStorage(settings);
      this.getAllSettings().subscribe((settings) => {
        this.saveToLocalStorage(settings);
        this.startSettingSync();
        this.getSettingsSyncInterval();
        resolve();
      });
    });
  }

  getSettingValue(name: string) {
    const settings: ISetting[] = JSON.parse(localStorage.getItem('settings'));

    const setting = settings ? settings.find((x) => x.name === name) : null;

    if (!setting) {
      return environment[name];
    }

    return setting.value;
  }

  getSetting(name: string) {
    const settings: ISetting[] = JSON.parse(localStorage.getItem('settings'));

    const setting = settings ? settings.find((x) => x.name === name) : null;

    if (!setting) {
      return environment[name];
    }

    return setting;
  }

  getKronosLoginSetting() {
    const setting = this.getSetting(Settings.KRONOS_LOGIN_SCREEN);

    if(setting.value.indexOf('true') >= 0){
      return true;
    } 

    return false;
  }

  getSettingParsed(name: string): ISettingOption {
    const settings: ISetting[] = JSON.parse(localStorage.getItem('settings'));

    const setting = settings ? settings.find((x) => x.name === name) : null;

    if (!setting) {
      return environment[name];
    }

    return JSON.parse(setting.value);
  }

  getBooleanSetting(name: string): boolean {
    return JSON.parse(this.getSettingValue(name)).value.toLowerCase() === 'true';
  }

  getSettingNumber(name: string) {
    return +this.getSettingValue(name);
  }

  getSettingObjectParsed(name: string): ISettingOption[] {
    var settingValue = this.getSettingValue(name);

    if (settingValue === undefined || settingValue === '') {
      return [];
    }

    var setting: ISettingOption[] = JSON.parse(settingValue);

    return setting;
  }

  getAll(): Observable<ISettingGroup[]> {
    return new Observable((observer) => {
      this.http.get<ISettingGroup[]>(`${this.conf.config.baseUrl}/settings`).subscribe(
        (settings) => {
                    observer.next(settings);
        },
        (error) => observer.error(error)
      );
    });
  }

  getFeatures(): Observable<string[]> {
    return this.http.get<string[]>(`${this.conf.config.baseUrl}/settings/getfeatures`);
  }

  getProductCategories(): Observable<string[]> {
    return this.http.get<string[]>(`${this.conf.config.baseUrl}/settings/getproductcategories`);
  }

  getAllSettings(): Observable<ISetting[]> {
    return new Observable((observer) => {
      const device = this.deviceService.getDeviceFromLocalStorage();

      if (device && device.institutionId) {
        const hospital = device.institutionId;
        this.http.get<ISetting[]>(`${this.conf.config.baseUrl}/settings/${hospital}`).subscribe(
          (settings) => {
            this.saveToLocalStorage(settings);
            observer.next(settings);
          },
          () => {
            const settings = this.setDefaultSettings();
            observer.next(settings);
          }
        );
      } else {
        this.http.get<ISetting[]>(`${this.conf.config.baseUrl}/settings/global`).subscribe(
          (settings) => {
            this.mergeDefaultSettings(settings);
            this.saveToLocalStorage(settings);
            observer.next(settings);
          },
          () => {
            const settings = this.setDefaultSettings();
            observer.next(settings);
          }
        );
      }
    });
  }

  enableCustomKeyboard(): boolean {
    if (this.deviceService.isBackOfficeDevice()) {
      return false;
    }
    
    const settings = this.getSettingObjectParsed(Settings.ENABLE_CUSTOM_KEYBOARD);
    const institutionId = this.deviceService.getInstitutionId();
    const isCustomKeyboardEnabledForThisHospital = settings.some((x) => x.value == institutionId.toString());

    return isCustomKeyboardEnabledForThisHospital;
  }

  enableDelayedSubmission(): boolean {    
    const settings = this.getSettingObjectParsed(Settings.DELAY_THEATRE_BASKET_SUBMISSION_BY_HOSPITAL);
    const institutionId = this.deviceService.getInstitutionId();
    const isDelayedSubmissionActivatedForHospital = settings.some((x) => x.value == institutionId.toString());

    return isDelayedSubmissionActivatedForHospital;
  }

  private mergeDefaultSettings(settings: ISetting[]) {
    const defaultSettings = this.getDefaultSettings();

    defaultSettings.forEach((defaultSetting) => {
      const settingExists = settings.find((x) => x.name === defaultSetting.name);
      if (!settingExists) {
        settings.push(defaultSetting);
      }
    });
  }
  update(setting: ISetting): Observable<boolean> {
    return new Observable((observer) => {
      this.http.put<string>(`${this.conf.config.baseUrl}/settings`, setting).subscribe(
        () => {
          this.getAll();
          observer.next(true);
        },
        (error) => {
          observer.error(error);
        }
      );
    });
  }

  settingDetailOptions(settingDetailOptionsUrl: string, institutionId: number): Observable<ISettingOption[]> {
    if (institutionId) settingDetailOptionsUrl = settingDetailOptionsUrl.replace('{institutionId}', institutionId.toString());

    return this.http.get<ISettingOption[]>(`${this.conf.config.baseUrl}/${settingDetailOptionsUrl}/`);
  }

  private getSettingsSyncInterval() {
    const settingsSyncInterval = this.getSettingValue(Settings.SETTINGS_SYNC_INTERVAL) * 1000;

    this.settingsSyncInterval.next(settingsSyncInterval);
  }

  private startSettingSync() {
    this.settingsSyncInterval.pipe(switchMap((settingsSyncInterval: number) => interval(settingsSyncInterval))).subscribe(() => {
      this.getSettings();
    });
  }

  private getSettings() {
    this.getAllSettings().subscribe((settings) => {
      this.saveToLocalStorage(settings);
    });
  }

  private saveToLocalStorage(settings: ISetting[]) {
    const currentSettings: ISetting[] = JSON.parse(localStorage.getItem('settings'));

    localStorage.setItem('settings', JSON.stringify(settings));

    const changedSettings: ISetting[] = [];

    if (settings !== null) {
      settings.forEach((setting) => {
        if (currentSettings) {
          const currentSetting = currentSettings.find((x) => x.name === setting.name);

          if (currentSetting && currentSetting.value !== setting.value) {
            changedSettings.push(setting);
          }
        }
      });
    }

    this.getSettingsSyncInterval();

    if (changedSettings !== null && changedSettings.length > 0) {
      this.settingsUpdated.next(changedSettings);
    }
  }

  hasSettingBeenUpdated(name: string, settings: ISetting[]) {
    if (settings === null || settings.length === 0) {
      return false;
    }

    const doesSettingExist = settings.find((x) => x.name === name);

    return !!doesSettingExist;
  }

  private setDefaultSettings() {
    if (localStorage.getItem('settings') !== null) {
      return JSON.parse(localStorage.getItem('settings'));
    }
    const settings = this.getDefaultSettings();

    localStorage.setItem('settings', JSON.stringify(settings));
    return settings;
  }

  private getDefaultSettings(): ISetting[] {
    const settings: ISetting[] = [
      new ISetting({
        id: 7,
        name: Settings.BASKET_AUTO_SUBMIT_DELAY,
        displayName: 'Basket Auth Submit Delay',
        type: 'Minutes',
        value: '300',
      }),
      new ISetting({ id: 21, name: Settings.BASKET_RETRY_INTERVAL, displayName: 'Basket Retry Interval', type: 'Seconds', value: '60' }),
      new ISetting({ id: 2, name: Settings.INACTIVITY_TIMEOUT, displayName: 'Inactivity timeout', type: 'Minutes', value: '120' }),
      new ISetting({ id: 22, name: Settings.BASKET_TIME_TO_LIVE, displayName: 'Basket time to live', type: 'Days', value: '604800' }),
      new ISetting({
        id: 31,
        name: Settings.DEACTIVATE_DEVICE_INTERVAL,
        displayName: 'Deactivate devices that are unused for certain periods of time',
        type: 'Days',
        value: '31104000',
      }),
      new ISetting({
        id: 23,
        name: Settings.PASSWORD_VALIDATOR,
        displayName: 'Password validation regex',
        value: '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})',
      }),
      new ISetting({ id: 5, name: Settings.SETTINGS_SYNC_INTERVAL, displayName: 'Settings Sync Interval', type: 'Minutes', value: '300' }),
      new ISetting({
        id: 24,
        name: Settings.DISCHARGED_PATIENT_DAYS_TO_SUBTRACT,
        displayName: 'Discharged Patients Days To Subtract',
        value: '5',
      }),
      new ISetting({
        id: 32,
        name: Settings.DEVICE_STATUS_CHECK,
        displayName: 'Refresh device activation status on client device',
        type: 'Minutes',
        value: '300',
      }),
      new ISetting({
        id: 11,
        name: Settings.APPLICATION_UPDATE_INTERVAL,
        displayName: 'Application update interval',
        type: 'Minutes',
        value: '300',
      }),
      new ISetting({
        id: 35,
        name: Settings.WHITEBOARD_REFRESH_INTERVAL,
        displayName: 'Whiteboard refresh interval',
        type: 'Minutes',
        value: '300',
      }),
      new ISetting({ id: 19, name: Settings.FORCE_CLIENT_UPDATE, displayName: 'Force client update', value: '1.4' }),
      new ISetting({ id: 36, name: Settings.ENABLE_INDEXED_DB, displayName: 'Enable index db', value: this.booleanDefaultSetting(false) }),
      new ISetting({
        id: 37,
        name: Settings.DELAY_THEATRE_BASKET_SUBMISSION_BY_HOSPITAL,
        displayName: 'Delay theatre basket submission',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 38,
        name: Settings.ACCESS_FREE_TEXT_PROCEDURE_VIA_PROCEDURE_LIST,
        displayName: 'Access Free Text Procedure Via Procedure List',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 39,
        name: Settings.ENABLE_DEFAULT_PROCEDURE_MATERIALS,
        displayName: 'Enable default materials and services when scanning patient out of theatre',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 40,
        name: Settings.AUTO_APPROVE_TO_SAME_LOCATION,
        displayName: 'Auto Approve To Same Location If Not Hostpital Return Location',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 41,
        name: Settings.AUTO_START_THEATRE_TIMER,
        displayName: 'Auto Start Theatre Timer',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 42,
        name: Settings.CAN_RETURN_TO_ANY_LOCATION,
        displayName: 'Can Return To Any Location',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 43,
        name: Settings.DELAY_THEATRE_BASKET_SUBMISSION_TIME,
        displayName: 'Delay Theatre Basket Submission Time',
        type: 'Hours',
        value: '2',
      }),
      new ISetting({
        id: 44,
        name: Settings.ENABLE_ACTION_CENTER,
        displayName: 'Enable Stock Transfer',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 45,
        name: Settings.ENABLE_ADD_PACK_WITH_UNAVAILABLE_MATERIALS,
        displayName: 'Enable Add Pack with Unavailable Materials',
        value: this.booleanDefaultSetting(true),
      }),
      new ISetting({
        id: 46,
        name: Settings.ENABLE_FREE_TEXT_THEATRE_PROCEDURE,
        displayName: 'Enable Free Text Theatre Procedure',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 47,
        name: Settings.ENABLE_PACK_MATERIAL_DECREASE_QUANTITY,
        displayName: 'Enable Pack Material Decrease Quantity',
        value: this.booleanDefaultSetting(true),
      }),
      new ISetting({
        id: 48,
        name: Settings.ENABLE_PACK_MATERIAL_DELETION,
        displayName: 'Enable Pack Material Deletion',
        value: this.booleanDefaultSetting(true),
      }),
      new ISetting({
        id: 49,
        name: Settings.ENABLE_PACK_MATERIAL_INCREASE_QUANTITY,
        displayName: 'Enable Pack Material Increase Quantity',
        value: this.booleanDefaultSetting(true),
      }),
      new ISetting({
        id: 50,
        name: Settings.ENABLE_THEATRE_VIEW_ON_SMALL_DEVICE,
        displayName: 'Enable Theatre View On Small Device',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 51,
        name: Settings.WHITEBOARD_EMPTY_BEDS_ENABLED,
        displayName: 'Enable Whiteboard Empty Beds',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 52,
        name: Settings.GET_DOCTORS_INTERVAL,
        displayName: 'Frequency of requesting the list of doctors in a ward',
        type: 'Minutes',
        value: '5',
      }),
      new ISetting({
        id: 53,
        name: Settings.GET_MATERIALS_IN_WARD_INTERVAL,
        displayName: 'Frequency of requesting the list of materials in a ward',
        type: 'Minutes',
        value: '5',
      }),
      new ISetting({
        id: 54,
        name: Settings.GET_PATIENTS_IN_WARD_INTERVAL,
        displayName: 'Frequency of requesting the list of patients in a ward',
        type: 'Minutes',
        value: '5',
      }),
      new ISetting({
        id: 55,
        name: Settings.GET_PROCEDURES_INTERVAL,
        displayName: 'Enable Whiteboard Empty Beds',
        type: 'Minutes',
        value: '5',
      }),
      new ISetting({
        id: 56,
        name: Settings.GET_ROOMS_IN_WARD_INTERVAL,
        displayName: 'Rooms update interval',
        type: 'Minutes',
        value: '60',
      }),
      new ISetting({
        id: 57,
        name: Settings.GET_SERVICES_INTERVAL,
        displayName: 'Frequency of requesting the list of services',
        type: 'Minutes',
        value: '5',
      }),
      new ISetting({
        id: 58,
        name: Settings.HIDE_MATERIALS_NO_POSITIVE_STOCK_LEVEL,
        displayName: 'Hide materials that do not have a positive stock level',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({ id: 59, name: Settings.THEATRE_MAX_ANAESTHETISTS, displayName: 'Theatre max anaesthetist', value: '1' }),
      new ISetting({ id: 60, name: Settings.THEATRE_MAX_DOCTORS, displayName: 'Theatre max doctors', value: '1' }),
      new ISetting({ id: 61, name: Settings.THEATRE_MAX_PROCEDURES, displayName: 'Theatre max procedures', value: '1' }),
      new ISetting({
        id: 62,
        name: Settings.THEATRE_PACKS_ENABLED,
        displayName: 'Enable Theatre Packs Management',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 63,
        name: Settings.THEATRE_REFRESH_INTERVAL,
        displayName: 'Theatre refresh interval',
        type: 'Seconds',
        value: '600',
      }),
      new ISetting({ id: 64, name: Settings.USER_PIN_LENGTH, displayName: 'User Pin Length', value: '4' }),
      new ISetting({
        id: 66,
        name: Settings.ENABLE_OPERATING_ROOM_FILTER,
        displayName: 'Activates filtering between operating rooms',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 67,
        name: Settings.ENABLE_SELECTING_FROM_PROCEDURE_LIST,
        displayName: 'Enable Selecting From Procedure List',
        value: this.booleanDefaultSetting(true),
      }),
      new ISetting({ id: 60, name: Settings.MAX_PATIENTS_PER_OPERATING_ROOM, displayName: 'Max patients per operating room', value: '1' }),
      new ISetting({
        id: 100,
        name: Settings.DELAY_BETWEEN_SCANNING_IN_MILLISECONDS,
        displayName: 'Delay between scanning in Milliseconds',
        value: '0',
      }),
      new ISetting({
        id: 100,
        name: Settings.CASE_NUMBER_LEADING_PADDING_CHARACTER,
        displayName: 'Case Number Leading Padding Character',
        value: '0',
      }),
      new ISetting({ id: 100, name: Settings.CASE_NUMBER_LENGTH, displayName: 'Case Number Length', value: '10' }),
      new ISetting({
        id: 101,
        name: Settings.WARDS_TO_EXCLUDE_FROM_STOCK_TRANSFER,
        displayName: 'Wards To Exclude From Stock Transfer',
        value: '[]',
      }),
      new ISetting({
        id: 102,
        name: Settings.WARDS_TO_EXCLUDE_FROM_EMERGENCY_CUPBOARD,
        displayName: 'Wards To Exclude From Emergency Cupboard',
        value: '[]',
      }),
      new ISetting({
        id: 103,
        name: Settings.ROOMS_TO_SKIP_WAITING_AND_RECOVERY,
        displayName: 'Rooms to skip waiting and recovery',
        value: '[]',
      }),
      new ISetting({
        id: 105,
        name: Settings.ENABLE_CUSTOM_KEYBOARD,
        displayName: 'Use the custom keyboard instead of the native keyboard',
        value: this.objectDefaultSetting(),
      }),
      new ISetting({ id: 105, name: Settings.WARDS_TO_SYNC_MATERIALS, displayName: 'Wards to sync Materials', value: '[]' }),
      new ISetting({
        id: 106,
        name: Settings.ENABLE_PACKS_FOR_NORMAL_WARD_BILLING,
        displayName: 'Enable Packs For Normal Ward Billing',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 107,
        name: Settings.ENABLE_PACK_MANAGEMENT,
        displayName: 'Enable Pack Management',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 108,
        name: Settings.ENABLE_EMAILS_FOR_MATERIALS_WITH_NO_STOCK,
        displayName: 'Enable email alerts for materials with no stock',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 109,
        name: Settings.ENABLE_REQUEST_TRAINING,
        displayName: 'Enable Request Training',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 110,
        name: Settings.ENABLE_LINK_EXISTING_DEVICE,
        displayName: 'Enable Linking A New Device To An Existing Device',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 111,
        name: Settings.KRONOS_SCHEDULE,
        displayName: 'Employee MasterData From Kronos Schedule',
        value: '0 * * * *',
      }),
      new ISetting({
        id: 112,
        name: Settings.KRONOS_LOGIN_SCREEN,
        displayName: 'Enable kronos login screen',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 113,
        name: Settings.ENABLE_MY_VOICE_SURVEYS,
        displayName: 'Enable  My Voice Surveys',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 114,
        name: Settings.OTP_TIMEOUT,
        displayName: 'OTP Timeout Duration in Minutes',
        type: 'Minutes',
        value: '5'
      }),
      new ISetting({
        id: 115,
        name: Settings.ENABLE_AI,
        displayName: 'Enable AI',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 116,
        name: Settings.ENABLE_CHECKLIST,
        displayName: 'Enable Checklist',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 117,
        name: Settings.FORCE_COMPLETE_CHECKLIST_ON_RECOVERY,
        displayName: 'Force Complete Checklist On Recovery',
        value: this.booleanDefaultSetting(false),
      }),
      new ISetting({
        id: 118,
        name: Settings.TIS_SCORING_DELIVERY_REPORT_INTERVAL,
        displayName: 'TIS Scoring Delivery Report Interval',
        type: 'Days',
        value: '7'
      }),
    ];

    return settings;
  }

  booleanDefaultSetting(value: boolean): string {
    return JSON.stringify({ name: value.toString(), value: value.toString() });
  }

  objectDefaultSetting(): string {
    return JSON.stringify([]);
  }

  getVersion()  : Observable<any>{
    return this.http.get<string>(
        `${this.conf.config.baseUrl}/Version/GetVersion`);
  }

 
  
}


